/**
 * chunkReload — recover from stale code-split chunks after a deploy.
 *
 * Vite fingerprints every lazy route/chunk (e.g. `Settings-58pvy5Ax.js`). When a
 * new version deploys, those filenames change and the old files disappear from the
 * CDN. A browser tab that loaded the PREVIOUS build still holds the old chunk names
 * in memory; the next lazy navigation fetches a file that no longer exists and
 * throws one of several browser-specific errors:
 *   • Chrome/Edge:  "Failed to fetch dynamically imported module: …"
 *   • Firefox:      "error loading dynamically imported module: …"
 *   • Safari:       "Importing a module script failed."
 *   • SPA fallback: "…Expected a JavaScript module script but the server responded
 *                    with a MIME type of \"text/html\"" (the missing chunk fell
 *                    back to index.html — functions/assets/[[path]].ts now answers
 *                    404 instead, so this one should stop appearing)
 *
 * The cure is a reload onto the current build. **Which is not the same thing as
 * `location.reload()`**, and that difference is the whole reason this file was
 * rewritten on 22 Aug 2026.
 *
 * What the founder hit, on /settings: the calm "Updating to the latest version…"
 * screen stayed put, and pressing "Reload now" brought it straight back. Only
 * Ctrl+Shift+R fixed it. Three things had to be true at once, and all three were:
 *
 *  1. **A reload is served by the service worker, not by the network.** The worker
 *     precaches `index.html`; until a NEW worker installs and activates, every
 *     reload replays the SAME old shell, which asks for the SAME dead chunk. A
 *     hard reload works precisely because it bypasses the worker.
 *  2. **The old code nudged the worker and then tore the page down.** `reg.update()`
 *     was fired without being awaited and `location.reload()` ran in the same tick,
 *     so the update it was counting on rarely survived long enough to matter.
 *  3. **One failure spent the entire reload budget.** Vite's `vite:preloadError`
 *     and the React error boundary both called in for the same broken import, so
 *     the 2-per-minute allowance was gone after a single click and what remained
 *     was a spinner and a button that could not help.
 *
 * So a heal now *takes the old build off the device* before it reloads: give a new
 * worker a bounded moment to take over, delete the caches that pin the old shell,
 * unregister the worker so the next navigation must reach the network, and only
 * then reload. And it spends one go per FAILURE, not per report of it.
 *
 * Dependency-free and side-effect-guarded on purpose, so it can be dropped verbatim
 * into Karr Travel / Karr Lab too.
 */

const CHUNK_ERROR_RE =
    /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|dynamically imported module|Loading chunk \S+ failed|Loading CSS chunk|ChunkLoadError|module script failed|MIME type of (?:"|')?text\/html/i;

/** True when an error looks like a stale / failed code-split chunk load. */
export function isChunkLoadError(err: unknown): boolean {
    if (!err) return false;
    if (typeof err === 'string') return CHUNK_ERROR_RE.test(err);
    const e = err as { name?: string; message?: string };
    if (e.name === 'ChunkLoadError') return true;
    return typeof e.message === 'string' && CHUNK_ERROR_RE.test(e.message);
}

const RELOAD_KEY = 'karr:chunk-reloads';
const RELOAD_WINDOW_MS = 60_000;
const MAX_RELOADS = 2; // 1 for a plain stale chunk, +1 for service-worker lag

/** Longest we let a new service worker install before reloading anyway. */
const SW_UPDATE_BUDGET_MS = 2_500;
/** Hard ceiling on the whole purge. The reload happens even if a step hangs. */
const HEAL_BUDGET_MS = 5_000;

/**
 * Caches kept during a heal. `supabase-api-cache` holds offline READ data and has
 * nothing to do with which build is installed; wiping it would take an offline
 * user's own numbers away from them to fix a JavaScript file.
 */
const KEEP_CACHE_RE = /supabase/i;

/** A heal already running in this page. Stops one failure spending two goes. */
let healInFlight = false;

/** Resolve after `ms`, whatever else happens. */
function after(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Run `work`, but never let it take longer than `ms`, and never let it throw. */
async function bounded(work: () => Promise<unknown>, ms: number): Promise<void> {
    try {
        await Promise.race([work().catch(() => {}), after(ms)]);
    } catch {
        /* a heal step must never be the reason the reload does not happen */
    }
}

/**
 * Take the old build off this device, then reload.
 *
 * Order matters. The worker is asked to update FIRST, because if a new one is
 * already waiting that is the cheap path: it activates, `swUpdate.ts` sees
 * `controllerchange`, and the page comes back on the new build with its caches
 * intact. Only then do we delete and unregister, which is what guarantees the
 * reload reaches the network when no new worker exists yet — a tab that broke
 * mid-deploy, an install that failed, a worker that is simply stuck.
 */
async function purgeOldBuildAndReload(): Promise<void> {
    const ceiling = after(HEAL_BUDGET_MS);

    const purge = (async () => {
        const hasSW = typeof navigator !== 'undefined' && 'serviceWorker' in navigator;

        if (hasSW) {
            await bounded(async () => {
                const reg = await navigator.serviceWorker.getRegistration();
                await reg?.update();
            }, SW_UPDATE_BUDGET_MS);
        }

        // The precached index.html lives here. While it exists, a reload is
        // answered from disk with the very build that just failed.
        if (typeof caches !== 'undefined') {
            await bounded(async () => {
                const names = await caches.keys();
                await Promise.all(
                    names.filter((n) => !KEEP_CACHE_RE.test(n)).map((n) => caches.delete(n)),
                );
            }, HEAL_BUDGET_MS);
        }

        // With no worker registered the next navigation cannot be intercepted at
        // all. main.tsx registers a fresh one as soon as the new build boots.
        if (hasSW) {
            await bounded(async () => {
                const regs = await navigator.serviceWorker.getRegistrations();
                await Promise.all(regs.map((r) => r.unregister()));
            }, HEAL_BUDGET_MS);
        }
    })();

    await Promise.race([purge, ceiling]);
    window.location.reload();
}

/** The heals this tab has spent inside the rolling window. */
function recentAttempts(): number[] {
    let attempts: number[] = [];
    try {
        const parsed: unknown = JSON.parse(sessionStorage.getItem(RELOAD_KEY) || '[]');
        if (Array.isArray(parsed)) attempts = parsed.filter((t): t is number => typeof t === 'number');
    } catch {
        attempts = [];
    }
    const now = Date.now();
    return attempts.filter((t) => now - t < RELOAD_WINDOW_MS);
}

/**
 * Reload onto the current build, taking the old one off the device on the way.
 *
 * Bounded to {@link MAX_RELOADS} within a rolling {@link RELOAD_WINDOW_MS} window
 * so a chunk that is genuinely broken (not merely stale) cannot spin an infinite
 * reload loop — once the budget is spent the caller shows a calm "please reload"
 * state instead. The budget is spent per HEAL: a second call while one is already
 * running (Vite's preloadError and the error boundary both report the same failed
 * import) rides along with the first rather than costing another go.
 *
 * @param options.force Ignore the budget. For a button the person actually pressed
 *   — "Reload now" is the last thing standing between them and a dead screen, so
 *   it must always do the full heal, never the plain reload that could not work.
 * @returns true when a heal is under way, so a caller can render "one moment…".
 */
export function reloadForFreshChunks(options?: { force?: boolean }): boolean {
    if (typeof window === 'undefined') return false;
    if (healInFlight) return true;

    const recent = recentAttempts();
    if (!options?.force && recent.length >= MAX_RELOADS) return false; // budget spent — don't loop

    recent.push(Date.now());
    try {
        sessionStorage.setItem(RELOAD_KEY, JSON.stringify(recent));
    } catch {
        /* private mode / storage blocked — still attempt the heal below */
    }

    healInFlight = true;
    void purgeOldBuildAndReload();
    return true;
}

/**
 * Install global listeners that auto-recover from stale-chunk failures which escape
 * React (async imports, prefetches, non-render code). Call once at app startup.
 *
 * We do NOT preventDefault on `vite:preloadError`: letting Vite re-throw keeps the
 * error identifiable so the React error boundaries can render a calm "Updating…"
 * state (see RouteError / ErrorBoundary) if a heal is ever budget-capped.
 */
export function installChunkReloadHandler(): void {
    if (typeof window === 'undefined') return;
    // Vite's own signal for a failed modulepreload of a dynamic import.
    window.addEventListener('vite:preloadError', () => {
        reloadForFreshChunks();
    });
    // Cross-browser safety net: some failed imports surface only as a generic
    // window error or unhandled rejection, never as vite:preloadError.
    window.addEventListener('error', (e) => {
        if (isChunkLoadError(e.error ?? e.message)) reloadForFreshChunks();
    });
    window.addEventListener('unhandledrejection', (e) => {
        if (isChunkLoadError(e.reason)) reloadForFreshChunks();
    });
}
